<template>
  <v-dialog v-model="studentWheelStore.modalOpen" fullscreen scrim="white" class="student-wheel-dialog">
    <div class="student-wheel-overlay" data-testid="StudentWheelModal-overlay">
      <div class="student-wheel-toolbar">
        <div class="student-wheel-toolbar-row">
          <v-select
            v-model="selectedJournalId"
            variant="outlined"
            placeholder="Izvēlēties žurnālu"
            menu-icon="fa-solid fa-chevron-down"
            hide-details
            :items="journalOptions"
            :loading="loadingJournals"
            class="student-wheel-journal-select"
            data-testid="StudentWheelModal-journalSelect"
          />

          <v-btn
            variant="flat"
            rounded="pill"
            class="student-wheel-spin-btn px-4 font-weight-bold"
            color="yellowBack"
            :disabled="!hasActiveItems || isSpinning"
            data-testid="StudentWheelModal-spin"
            @click="spin"
          >
            Izsaukt
          </v-btn>
        </div>

        <div class="student-wheel-toolbar-separator">
          <span class="line" />
          <span class="text">vai</span>
          <span class="line" />
        </div>

        <v-textarea
          v-model="customNames"
          variant="outlined"
          placeholder="Ievadi vārdus katru jaunā rindā"
          hide-details
          rows="6"
          no-resize
          class="student-wheel-custom-names"
          data-testid="StudentWheelModal-customNames"
        />

        <div v-if="calledLabels.length > 0" class="student-wheel-called" data-testid="StudentWheelModal-called">
          <div class="student-wheel-called-header">
            <span class="student-wheel-called-title">Izsauktie</span>
            <button type="button" class="student-wheel-called-clear" data-testid="StudentWheelModal-clearCalled" @click="clearCalled">
              Notīrīt
            </button>
          </div>
          <div
            v-for="(label, index) in calledLabels"
            :key="label"
            class="student-wheel-called-item"
            data-testid="StudentWheelModal-calledItem"
          >
            <span class="student-wheel-called-index">{{ index + 1 }}.</span>
            <span class="student-wheel-called-name">{{ label }}</span>
            <button
              type="button"
              class="student-wheel-called-remove"
              data-testid="StudentWheelModal-removeCalled"
              :aria-label="`Atgriezt ${label}`"
              @click="removeCalled(label)"
            >
              <v-icon size="10">fa-light fa-xmark</v-icon>
            </button>
          </div>
        </div>
      </div>

      <div class="student-wheel-center" @click="spin">
        <div class="student-wheel-pointer-top" />
        <div ref="wheelContainer" data-testid="StudentWheelModal-wheel" class="student-wheel-container" />
        <div v-if="selectedStudent" class="student-wheel-result" data-testid="StudentWheelModal-result">
          <span class="student-wheel-result-name">{{ selectedStudent }}</span>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script lang="ts">
import confetti from "canvas-confetti"
import { Wheel } from "spin-wheel"

import { useTeacherToolsScrimClip } from "~/composables/useTeacherToolsScrimClip"
import { buildUniqueNameLabels, findFullName } from "~/helpers/uniqueNameLabels"
import storage from "~/services/storage"
import { useEklaseStore } from "~/stores/eklaseStore"
import { useStudentWheelStore } from "~/stores/studentWheelStore"

const WHEEL_COLORS = [
  "#F9D805", "#FFF1AE",
]

const DEFAULT_ITEMS_COUNT = 20

function buildPlaceholderItems() {
  return Array.from({ length: DEFAULT_ITEMS_COUNT }, () => ({ label: "" }))
}

export default {
  setup() {
    const eklaseStore = useEklaseStore()
    const studentWheelStore = useStudentWheelStore()
    const scrimClip = useTeacherToolsScrimClip(".student-wheel-dialog")
    return { eklaseStore, studentWheelStore, ...scrimClip }
  },

  data() {
    return {
      selectedJournalId: null as number | null,
      customNames: "",
      loadingJournals: true,
      isSpinning: false,
      selectedStudent: null as string | null,
      wheelInstance: null as Wheel | null,
      calledLabels: [] as string[],
    }
  },

  computed: {
    journalOptions(): { title: string; value: number }[] {
      return this.eklaseStore.journals.map((journal) => ({
        title: journal.eklase_name,
        value: journal.id,
      }))
    },

    students() {
      const journal = this.eklaseStore.journals.find((j) => j.id === this.selectedJournalId)
      if (!journal) { return [] }
      return journal.students.filter((s) => s.active)
    },

    customNameLines(): string[] {
      return this.customNames
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
    },

    wheelItems(): string[] {
      if (this.customNameLines.length > 0) { return this.customNameLines }
      return buildUniqueNameLabels(this.students)
    },

    wheelDisplayItems(): { label: string; backgroundColor?: string; labelColor?: string }[] {
      return this.wheelItems.map((label) => (
        this.calledLabels.includes(label)
          ? { label, backgroundColor: "#D4D4D4", labelColor: "#999999" }
          : { label }
      ))
    },

    hasActiveItems(): boolean {
      return this.wheelItems.some((label) => !this.calledLabels.includes(label))
    },
  },

  watch: {
    "studentWheelStore.modalOpen"(isOpen: boolean) {
      if (isOpen) {
        this.fetchJournals()
        this.restoreJournalFromStorage()
        this.$nextTick(() => {
          this.buildWheel()
          this.startScrimClipObserver()
        })
      } else {
        this.stopScrimClipObserver()
        this.reset()
      }
    },

    selectedJournalId(newId: number | null) {
      if (newId !== null) {
        storage.setStudentWheelJournalId(newId)
        this.customNames = ""
      }
      this.$nextTick(() => this.buildWheel())
    },

    wheelItems() {
      this.calledLabels = []
      if (this.studentWheelStore.modalOpen) {
        this.$nextTick(() => this.buildWheel())
      }
    },
  },

  methods: {
    fetchJournals() {
      if (this.eklaseStore.journals.length > 0) {
        this.loadingJournals = false
        return
      }

      this.loadingJournals = true
      this.eklaseStore.fetchJournals({
        successCallback: () => {
          this.loadingJournals = false
          this.restoreJournalFromStorage()
        },
        errorCallback: () => {
          this.loadingJournals = false
        },
      })
    },

    restoreJournalFromStorage() {
      if (this.selectedJournalId) { return }
      const id = storage.getStudentWheelJournalId()
      if (!id) { return }
      if (this.eklaseStore.journals.some((j) => j.id === id)) {
        this.selectedJournalId = id
      }
    },

    buildWheel() {
      const container = this.$refs.wheelContainer as HTMLElement | null
      if (!container) { return }

      if (this.wheelInstance) {
        this.wheelInstance.remove()
        this.wheelInstance = null
      }

      const items = this.wheelItems.length > 0
        ? this.wheelDisplayItems
        : buildPlaceholderItems()

      this.wheelInstance = new Wheel(container, {
        items,
        itemLabelRadiusMax: 0.35,
        itemLabelFontSizeMax: 16,
        itemLabelAlign: "right",
        borderColor: "#FFFFFF",
        borderWidth: 2,
        lineColor: "#FFFFFF",
        lineWidth: 1,
        radius: 0.9,
        isInteractive: false,
        itemBackgroundColors: WHEEL_COLORS,
        itemLabelColors: ["#221B00"],
        pointerAngle: 0,
      })

      this.isSpinning = false
      this.selectedStudent = null
    },

    spin() {
      if (!this.wheelInstance || !this.hasActiveItems || this.isSpinning) { return }

      const activeIndices = this.wheelItems
        .map((label, index) => (this.calledLabels.includes(label) ? -1 : index))
        .filter((index) => index !== -1)
      const winnerIndex = activeIndices[Math.floor(Math.random() * activeIndices.length)]
      const winnerLabel = this.wheelItems[winnerIndex]

      this.isSpinning = true
      this.selectedStudent = null

      this.wheelInstance.onRest = () => {
        if (this.customNameLines.length > 0) {
          this.selectedStudent = winnerLabel
        } else {
          this.selectedStudent = findFullName(this.students, winnerLabel)
        }
        this.isSpinning = false
        this.calledLabels.push(winnerLabel)
        this.syncWheelColors()
        this.fireConfetti()
      }

      this.wheelInstance.spinToItem(winnerIndex, 3000, true, 5, 1)
    },

    syncWheelColors() {
      if (this.wheelInstance && this.wheelItems.length > 0) {
        this.wheelInstance.items = this.wheelDisplayItems
      }
    },

    clearCalled() {
      this.calledLabels = []
      this.syncWheelColors()
    },

    removeCalled(label: string) {
      this.calledLabels = this.calledLabels.filter((called) => called !== label)
      this.syncWheelColors()
    },

    fireConfetti() {
      const somaColors = ["#F9D805", "#FFF1AE", "#221B00", "#FFFFFF"]
      const burst = { particleCount: 80, spread: 70, colors: somaColors, zIndex: 2500 }

      confetti({ ...burst, angle: 60, origin: { x: 0, y: 0.6 } })
      confetti({ ...burst, angle: 120, origin: { x: 1, y: 0.6 } })
    },

    reset() {
      if (this.wheelInstance) {
        this.wheelInstance.remove()
        this.wheelInstance = null
      }
      this.selectedJournalId = null
      this.customNames = ""
      this.isSpinning = false
      this.selectedStudent = null
      this.calledLabels = []
    },
  },
}
</script>

<style lang="sass" scoped>
.student-wheel-dialog :deep(.v-overlay__scrim)
  background: rgba(255, 255, 255, 0.75) !important
  opacity: 1 !important

  &::before
    content: ""
    position: absolute
    inset: 0
    backdrop-filter: blur(8px)
    z-index: -1

.student-wheel-dialog :deep(.v-overlay__content)
  background: transparent !important
  pointer-events: none
.student-wheel-overlay
  width: 100vw
  height: 100vh
  display: flex
  flex-direction: column
  align-items: center
  pointer-events: none

.student-wheel-toolbar
  position: absolute
  top: 72px
  right: 16px
  display: flex
  flex-direction: column
  align-items: stretch
  gap: 12px
  z-index: 1
  pointer-events: auto

.student-wheel-toolbar-row
  display: flex
  align-items: center
  gap: 12px

.student-wheel-toolbar-separator
  display: flex
  align-items: center
  gap: 8px
  color: rgba(0, 0, 0, 0.5)
  font-size: 14px

  .line
    flex: 1
    height: 1px
    background: rgba(0, 0, 0, 0.15)

.student-wheel-custom-names
  width: 100%

  :deep(.v-field)
    background: #FFFFFF

.student-wheel-called
  display: flex
  flex-direction: column
  gap: 2px
  background-color: #FFFFFF
  border-radius: 8px
  padding: 10px 8px
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08)

.student-wheel-called-header
  display: flex
  align-items: center
  justify-content: space-between
  padding: 0 8px
  margin-bottom: 2px

.student-wheel-called-title
  font-size: 12px
  font-weight: 500
  color: #9CA3AF
  text-transform: uppercase
  letter-spacing: 0.05em

.student-wheel-called-clear
  font-size: 12px
  color: #9CA3AF
  background: none
  border: none
  padding: 0
  cursor: pointer
  transition: color 0.15s

  &:hover
    color: #4B5563

.student-wheel-called-item
  display: flex
  align-items: center
  gap: 8px
  padding: 2px 8px
  border-radius: 4px

  &:hover .student-wheel-called-remove
    opacity: 1

.student-wheel-called-index
  font-size: 12px
  color: #D1D5DB
  min-width: 14px
  text-align: right
  flex-shrink: 0

.student-wheel-called-name
  font-size: 14px
  color: #9CA3AF
  flex: 1

.student-wheel-called-remove
  display: flex
  align-items: center
  background: none
  border: none
  padding: 0
  color: #D1D5DB
  cursor: pointer
  opacity: 0
  transition: opacity 0.15s, color 0.15s
  flex-shrink: 0

  &:hover
    color: #6B7280

:deep(.student-wheel-journal-select)
  min-width: 256px
  .v-field__field, .v-field__append-inner
    height: 44px
  .v-field__input
    font-weight: 400
    font-size: 16px
    line-height: 44px
    padding-top: 0
    padding-bottom: 0
    min-height: 44px

.student-wheel-spin-btn
  height: 44px
  text-transform: none
  font-size: 16px !important
  line-height: 20px !important
  letter-spacing: 0.1px

  &.v-btn--disabled
    background: rgba(0, 0, 0, 0.12) !important
    color: rgba(0, 0, 0, 0.38) !important

// pointer-events only on the wheel itself, or this stretched area swallows clicks aimed at the toolbar behind the scrim
.student-wheel-center
  flex: 1
  display: flex
  align-items: center
  justify-content: center
  position: relative
  pointer-events: none

.student-wheel-container
  width: 620px
  height: 620px
  pointer-events: auto
  cursor: pointer

.student-wheel-pointer-top
  position: absolute
  top: calc(50% - 320px)
  left: 50%
  transform: translateX(-50%)
  width: 0
  height: 0
  border-left: 14px solid transparent
  border-right: 14px solid transparent
  border-top: 22px solid #221B00
  z-index: 1

.student-wheel-result
  position: absolute
  top: 50%
  left: 50%
  transform: translate(-50%, -50%)
  z-index: 2
  pointer-events: none

.student-wheel-result-name
  background: #221B00
  color: #F9D805
  font-size: 28px
  font-weight: 700
  padding: 14px 32px
  border-radius: 50px
  white-space: nowrap
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.3)
</style>
